import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";

interface LoadingOverlayProps {
  onDone: () => void;
}

const STEP_INTERVAL = 620;

export function LoadingOverlay({ onDone }: LoadingOverlayProps) {
  const { t } = useTranslation("auth");
  const steps = t("login.loadingSteps", { returnObjects: true }) as string[];
  const [step, setStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const handleDone = useCallback(onDone, [onDone]);

  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => {
        if (s >= steps.length - 1) {
          clearInterval(id);
          setIsComplete(true);
          // Pause briefly on "Ready." then call onDone
          setTimeout(handleDone, 500);
          return s;
        }
        return s + 1;
      });
    }, STEP_INTERVAL);
    return () => clearInterval(id);
  }, [steps.length, handleDone]);

  // Progress percentage derived from current step
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-canvas">
      {/* Soft radial glow behind logo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(600px 400px at 50% 45%, var(--brand-soft), transparent 70%)",
        }}
      />

      {/* Logo reveal */}
      <motion.div
        className="relative flex items-center justify-center h-14 z-10"
        initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <img
          src={logoDark}
          alt="WiServices"
          className="h-11 block dark:block hidden"
        />
        <img
          src={logoLight}
          alt="WiServices"
          className="h-11 block dark:hidden"
        />
      </motion.div>

      {/* Progress bar */}
      <motion.div
        className="w-[220px] h-[3px] rounded-sm bg-hairline overflow-hidden relative mt-7 z-10"
        initial={{ opacity: 0, scaleX: 0.6 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        {/* Filled track */}
        <motion.div
          className="absolute inset-y-0 left-0 rounded-sm"
          style={{
            background: `linear-gradient(90deg, var(--brand), var(--brand-hover))`,
          }}
          initial={{ width: "0%" }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: STEP_INTERVAL / 1000, ease: "easeOut" }}
        />
        {/* Shimmer pulse on top */}
        {!isComplete && (
          <motion.div
            className="absolute inset-y-0 w-[40%] rounded-sm"
            style={{
              background: `linear-gradient(90deg, transparent, var(--brand-hover), transparent)`,
              opacity: 0.5,
            }}
            animate={{ x: ["-100%", "350%"] }}
            transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}
      </motion.div>

      {/* Step text */}
      <div className="flex flex-col items-center mt-5 min-h-[36px] z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="flex items-center gap-2"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {isComplete && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
                className="flex items-center justify-center w-4 h-4 rounded-full bg-ok"
              >
                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </motion.span>
            )}
            <span
              className={`font-mono text-xs ${
                isComplete ? "text-ok" : "text-muted"
              }`}
            >
              {steps[step]}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Fade-out on complete */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            className="absolute inset-0 bg-canvas z-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
